import React, { useState, useRef } from "react";
import { Modal } from "react-bootstrap";
import "./index.css";
import { Order } from "../../../Payments/OrderRefund/ViewOrderRefund";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { db } from "../../../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";

interface PrintSetupModalProps {
  handleModalClose: () => void;
  showModal: boolean;
  orderList: Order[];
  cartName?: string;
  orderDate?: string;
  isAllCartsSelected?: boolean;
  isSummaryView?: boolean;
  menuItemCounts?: {
    name: string;
    totalCount: number;
    reservedCount: number;
  }[];
}

const PrintSetupModal: React.FC<PrintSetupModalProps> = ({
  showModal,
  handleModalClose,
  orderList,
  cartName,
  orderDate,
  isAllCartsSelected = false,
  isSummaryView = false,
  menuItemCounts = [],
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const isDownloadingRef = useRef(false);
  const isDownloadingExcelRef = useRef(false);
  const formattedDate = orderDate ? orderDate.split("T")[0] : "";

  const getCustomerName = (order: any) => {
    if (order.orderCreatedBy === "CartPerson") return "Cart Person";
    if (order.orderCreatedBy === "Customer") return order.customer?.displayName || "Customer";
    if (order.orderCreatedBy === "Restaurant") {
      if (order.organization?.displayName) return order.organization.displayName;
      if (order.customer?.displayName) return order.customer.displayName;
      return "Restaurant";
    }
    return order.orderCreatedBy;
  };

  const getItemsSummary = (order: any) =>
    order.orderItems
      .map(
        (item: any) =>
          `${item.displayName} x ${item.quantity} (Rs. ${(item.unitPrice || 0).toFixed(2)})`
      )
      .join("; ");

  const getPaymentMethod = (order: any) => {
    if ((order as any).promoCode) return "Promo";
    return order.paymentMethod || "";
  };

  const getAgentName = (order: any) => (order as any).orderTakenBy?.displayName || "";

  // Filter out cancelled and tentative orders (case-insensitive)
  const filteredOrders = orderList.filter((order) => {
    const status = (order.orderStatus || "").toString().toLowerCase();
    return status !== "cancelled" && status !== "tentative";
  });

  // Group orders by cart when "All" is selected (excluding Restaurant orders)
  const groupedOrders = isAllCartsSelected
    ? filteredOrders.reduce((acc: any, order) => {
        const cartDisplayName =
          order.cart?.displayName ||
          (order.orderCreatedBy === "Restaurant"
            ? "Restaurant Orders"
            : "Unknown Cart");
        if (!acc[cartDisplayName]) {
          acc[cartDisplayName] = [];
        }
        acc[cartDisplayName].push(order);
        return acc;
      }, {})
    : null;

  // Splits an array into chunks of a given size — Firestore's "in" operator
  // only accepts up to 10 values per query.
  const chunkArray = <T,>(arr: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
    return chunks;
  };

  // Fetches the Transactions collection (a separate top-level collection,
  // linked to Orders only via the `orderId` field) and returns a map of
  // orderId -> transaction amount. When an order has more than one
  // Transaction record, a "Completed" one is preferred; otherwise whichever
  // is encountered first is kept.
  const fetchTransactionAmountsByOrderId = async (
    orderIds: string[]
  ): Promise<Record<string, number>> => {
    const best: Record<string, { amount: number; status?: string }> = {};
    const uniqueIds = Array.from(new Set(orderIds.filter(Boolean)));
    const chunks = chunkArray(uniqueIds, 10);

    for (const chunk of chunks) {
      if (chunk.length === 0) continue;
      const q = query(collection(db, "Transactions"), where("orderId", "in", chunk));
      const snap = await getDocs(q);
      snap.forEach((docSnap) => {
        const data: any = docSnap.data();
        const oid = data.orderId;
        const amount = typeof data.amount === "number" ? data.amount : parseFloat(data.amount);
        if (!oid || Number.isNaN(amount)) return;

        const isCompleted = data.status === "Completed";
        if (!best[oid] || (isCompleted && best[oid].status !== "Completed")) {
          best[oid] = { amount, status: data.status };
        }
      });
    }

    const result: Record<string, number> = {};
    Object.entries(best).forEach(([oid, v]) => {
      result[oid] = v.amount;
    });
    return result;
  };

  // Returns the transaction amount only when it differs from the order's
  // net amount (beyond floating-point rounding); otherwise returns "" so
  // the Excel cell stays blank for orders that reconcile correctly.
  const getTransactionMismatch = (
    order: any,
    transactionAmountsByOrderId: Record<string, number>
  ) => {
    const txAmount = transactionAmountsByOrderId[order.id];
    if (txAmount === undefined) return "";
    const netAmount = order.paymentSummary?.netAmount || 0;
    if (Math.abs(txAmount - netAmount) < 0.01) return "";
    return parseFloat(txAmount.toFixed(2));
  };

  const handleDownloadPdf = async () => {
    if (isDownloadingRef.current) return;
    isDownloadingRef.current = true;
    setIsDownloading(true);

    try {
      const pdf = isSummaryView
        ? new jsPDF("p", "mm", "a4")
        : new jsPDF("l", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();

      if (isSummaryView) {
        // Handle summary view printing
        const tempDiv = document.createElement("div");
        tempDiv.style.position = "absolute";
        tempDiv.style.left = "-9999px";
        tempDiv.style.width = "210mm";
        tempDiv.style.padding = "20px";
        tempDiv.style.backgroundColor = "white";

        tempDiv.innerHTML = `
  <div style="text-align: center; margin-bottom: 20px; font-size: 18px; font-weight: bold;">
    Menu Item Summary ${
      isAllCartsSelected ? "For All Carts" : cartName ? `For ${cartName}` : ""
    } on ${formattedDate}
  </div>
  <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
    <thead>
      <tr style="background-color: #f8f9fa;">
        <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">#</th>
        <th style="border: 1px solid #dee2e6; padding: 8px; text-align: left;">Menu Item</th>
        <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Total Quantity</th>
        <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Reserved Items</th>
        <th style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">Available Quantity</th>
      </tr>
    </thead>
    <tbody>
      ${menuItemCounts
        .map(
          (item, index) => `
        <tr>
          <td style="border: 1px solid #dee2e6; padding: 8px;">${index + 1}</td>
          <td style="border: 1px solid #dee2e6; padding: 8px;">${item.name}</td>
          <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${
            item.totalCount
          }</td>
          <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${
            item.reservedCount
          }</td>
          <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${
            item.totalCount - item.reservedCount
          }</td>
        </tr>
      `
        )
        .join("")}
      <tr style="font-weight: bold;">
        <td colspan="2" style="border: 1px solid #dee2e6; padding: 8px; text-align: right;">Total Items:</td>
        <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
          ${menuItemCounts.reduce((sum, item) => sum + item.totalCount, 0)}
        </td>
        <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
          ${menuItemCounts.reduce((sum, item) => sum + item.reservedCount, 0)}
        </td>
        <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">
          ${menuItemCounts.reduce(
            (sum, item) => sum + (item.totalCount - item.reservedCount),
            0
          )}
        </td>
      </tr>
    </tbody>
  </table>
`;

        document.body.appendChild(tempDiv);

        const canvas = await html2canvas(tempDiv, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const imgData = canvas.toDataURL("image/png");
        const imgProps = pdf.getImageProperties(imgData);
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        document.body.removeChild(tempDiv);

        pdf.save(`Menu_Item_Summary_${formattedDate}.pdf`);
      } else if (isAllCartsSelected && groupedOrders) {
        // Generate separate pages for each cart in order view
        const cartNames = Object.keys(groupedOrders);

        for (let cartIndex = 0; cartIndex < cartNames.length; cartIndex++) {
          const cartDisplayName = cartNames[cartIndex];
          const orders = groupedOrders[cartDisplayName];
          const ordersPerPage = 15; // Number of orders per page

          // Split orders into chunks for pagination
          const orderChunks = [];
          for (let i = 0; i < orders.length; i += ordersPerPage) {
            orderChunks.push(orders.slice(i, i + ordersPerPage));
          }

          for (
            let chunkIndex = 0;
            chunkIndex < orderChunks.length;
            chunkIndex++
          ) {
            const currentOrders = orderChunks[chunkIndex];
            const isFirstChunk = chunkIndex === 0;
            const isLastChunk = chunkIndex === orderChunks.length - 1;

            // Create a temporary div for each page
            const tempDiv = document.createElement("div");
            tempDiv.style.position = "fixed";
            tempDiv.style.top = "-9999px";
            tempDiv.style.left = "-9999px";
            tempDiv.style.width = "297mm";
            tempDiv.style.padding = "15px";
            tempDiv.style.backgroundColor = "white";
            tempDiv.style.fontSize = "10px";

            // Header (only show on first page for each cart)
            const header = isFirstChunk
              ? `
        <div style="text-align: center; margin-bottom: 20px; font-size: 18px; font-weight: bold;">
          Order Summary For ${cartDisplayName} on ${formattedDate}
        </div>
      `
              : "";

            // Table header
        const tableHeader = `
        <table style="width: 100%; border-collapse: collapse; font-size: 9px; table-layout: fixed;">
          <thead>
            <tr style="background-color: #f8f9fa;">
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 4%;">#</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 10%;">Order Ref</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 12%;">Customer</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 22%;">Items Ordered</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 9%;">Order Value</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 8%;">Discount</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 9%;">Net Amount</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 10%;">Payment Method</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 10%;">Agent</th>
              <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 6%;">Issued</th>
            </tr>
          </thead>
          <tbody>
      `;

            // Table footer (only show on last page for each cart)
        const tableFooter = isLastChunk
              ? `
            <tr style="font-weight: bold;">
              <td colspan="4" style="border: 1px solid #dee2e6; padding: 6px; text-align: right;">Total:</td>
              <td style="border: 1px solid #dee2e6; padding: 6px;">
                Rs. ${orders
                  .reduce(
                    (sum: number, order: any) => sum + (order.paymentSummary?.totalPrice || 0),
                    0
                  )
                  .toFixed(2)}
              </td>
              <td style="border: 1px solid #dee2e6; padding: 6px;">
                Rs. ${orders
                  .reduce(
                    (sum: number, order: any) => sum + (order.paymentSummary?.discount || 0),
                    0
                  )
                  .toFixed(2)}
              </td>
              <td style="border: 1px solid #dee2e6; padding: 6px;">
                Rs. ${orders
                  .reduce(
                    (sum: number, order: any) => sum + (order.paymentSummary?.netAmount || 0),
                    0
                  )
                  .toFixed(2)}
              </td>
              <td style="border: 1px solid #dee2e6; padding: 6px;"></td>
              <td style="border: 1px solid #dee2e6; padding: 6px;"></td>
              <td style="border: 1px solid #dee2e6; padding: 6px;"></td>
            </tr>
          </tbody>
        </table>
      `
              : `
          </tbody>
        </table>
      `;

            // Build table rows with continuous numbering
        const tableRows = currentOrders
              .map(
                (order: any, index: number) => `
            <tr>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">${
                chunkIndex * ordersPerPage + index + 1
              }</td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">${
                order.orderReference
              }</td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                ${getCustomerName(order)}
              </td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                ${order.orderItems
                  .map(
                    (item: any) =>
                      `${item.displayName} x ${
                        item.quantity
                      } (Rs. ${(item.unitPrice || 0).toFixed(2)})`
                  )
                  .join("<br>")}
              </td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">Rs. ${(
                order.paymentSummary?.totalPrice || 0
              ).toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">Rs. ${(
                order.paymentSummary?.discount || 0
              ).toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">Rs. ${(
                order.paymentSummary?.netAmount || 0
              ).toFixed(2)}</td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">${getPaymentMethod(order)}</td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">${getAgentName(order)}</td>
              <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;"></td>
            </tr>
          `
              )
              .join("");

            // Combine all parts
            tempDiv.innerHTML = header + tableHeader + tableRows + tableFooter;
            document.body.appendChild(tempDiv);

            // Capture the page content
            const canvas = await html2canvas(tempDiv, {
              scale: 2,
              useCORS: true,
              allowTaint: true,
            });

            const imgData = canvas.toDataURL("image/png");
            const imgProps = pdf.getImageProperties(imgData);
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

            // Add new page if not the first page
            if (cartIndex > 0 || chunkIndex > 0) {
              pdf.addPage();
            }

            pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
            document.body.removeChild(tempDiv);
          }
        }

        pdf.save(`All_Carts_Orders_${formattedDate}.pdf`);
      } else {
        // Handle single cart with proper pagination
        const ordersPerPage = 15; // Adjust this number based on your content size
        const orderChunks = [];
        for (let i = 0; i < filteredOrders.length; i += ordersPerPage) {
          orderChunks.push(filteredOrders.slice(i, i + ordersPerPage));
        }

        for (let i = 0; i < orderChunks.length; i++) {
          const orders = orderChunks[i];
          
          const tempDiv = document.createElement("div");
          tempDiv.style.position = "fixed";
          tempDiv.style.top = "-9999px";
          tempDiv.style.left = "-9999px";
          tempDiv.style.width = "297mm";
          tempDiv.style.padding = "15px";
          tempDiv.style.backgroundColor = "white";
          tempDiv.style.fontSize = "10px";

          // Create the header
          const header = `
          <div style="text-align: center; margin-bottom: 20px; font-size: 18px; font-weight: bold;">
            Order Summary For ${cartName} on ${formattedDate}
          </div>
        `;

          // Create the table header
          const tableHeader = `
          <table style="width: 100%; border-collapse: collapse; font-size: 9px; table-layout: fixed; margin-bottom: 15px;">
            <thead>
               <tr style="background-color: #f8f9fa;">
                 <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 4%;">#</th>
                 <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 10%;">Order Ref</th>
                 <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 12%;">Customer</th>
                 <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 22%;">Items Ordered</th>
                 <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 9%;">Order Value</th>
                 <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 8%;">Discount</th>
                 <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 9%;">Net Amount</th>
                 <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 10%;">Payment Method</th>
                 <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 10%;">Agent</th>
                 <th style="border: 1px solid #dee2e6; padding: 6px; text-align: left; width: 6%;">Issued</th>
               </tr>
             </thead>
            <tbody>
        `;

          // Create the table footer with totals (only on last page)
          const tableFooter = i === orderChunks.length - 1 ? `
              <tr style="font-weight: bold;">
                 <td colspan="4" style="border: 1px solid #dee2e6; padding: 6px; text-align: right;">Total:</td>
                 <td style="border: 1px solid #dee2e6; padding: 6px;">
                   Rs. ${filteredOrders
                     .reduce((sum, order) => sum + (order.paymentSummary?.totalPrice || 0), 0)
                     .toFixed(2)}
                 </td>
                 <td style="border: 1px solid #dee2e6; padding: 6px;">
                   Rs. ${filteredOrders
                     .reduce((sum, order) => sum + (order.paymentSummary?.discount || 0), 0)
                     .toFixed(2)}
                 </td>
                 <td style="border: 1px solid #dee2e6; padding: 6px;">
                  Rs. ${filteredOrders
                    .reduce((sum, order) => sum + (order.paymentSummary?.netAmount || 0), 0)
                    .toFixed(2)}
                 </td>
                 <td style="border: 1px solid #dee2e6; padding: 6px;"></td>
                 <td style="border: 1px solid #dee2e6; padding: 6px;"></td>
                 <td style="border: 1px solid #dee2e6; padding: 6px;"></td>
              </tr>
            </tbody>
          </table>
        ` : `
            </tbody>
          </table>
        `;

          // Build the table rows for this chunk
          const tableRows = orders
            .map(
              (order, index) => `
                <tr>
                  <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">${
                    i * ordersPerPage + index + 1
                  }</td>
                  <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">${
                    order.orderReference
                  }</td>
                  <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                     ${getCustomerName(order)}
                  </td>
                  <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">
                    ${order.orderItems
                      .map(
                        (item: any) =>
                          `${item.displayName} x ${
                            item.quantity
                          } (Rs. ${(item.unitPrice || 0).toFixed(2)})`
                      )
                      .join("<br>")}
                  </td>
                  <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">Rs. ${(
                     order.paymentSummary?.totalPrice || 0
                   ).toFixed(2)}</td>
                   <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">Rs. ${(
                     order.paymentSummary?.discount || 0
                   ).toFixed(2)}</td>
                   <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">Rs. ${(
                    order.paymentSummary?.netAmount || 0
                  ).toFixed(2)}</td>
                  <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">${getPaymentMethod(order)}</td>
                  <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;">${getAgentName(order)}</td>
                  <td style="border: 1px solid #dee2e6; padding: 6px; word-wrap: break-word;"></td>
                </tr>
              `
            )
            .join("");

          // Combine all parts for this page
          tempDiv.innerHTML = header + tableHeader + tableRows + tableFooter;
          document.body.appendChild(tempDiv);

          const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
          });

          const imgData = canvas.toDataURL("image/png");
          const imgProps = pdf.getImageProperties(imgData);
          const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

          // Add new page for each chunk (except the first one)
          if (i > 0) {
            pdf.addPage();
          }

          pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

          // Clean up
          document.body.removeChild(tempDiv);
        }
        pdf.save(`${cartName}_Orders_${formattedDate}.pdf`);
      }

      setIsDownloading(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
      setIsDownloading(false);
    } finally {
      isDownloadingRef.current = false;
    }
  };

  const handleDownloadExcel = async () => {
    if (isDownloadingExcelRef.current) return;
    isDownloadingExcelRef.current = true;
    setIsDownloadingExcel(true);
    try {
      const wb = XLSX.utils.book_new();

      let transactionAmountsByOrderId: Record<string, number> = {};
          if (!isSummaryView) {
            try {
              transactionAmountsByOrderId = await fetchTransactionAmountsByOrderId(
                filteredOrders.map((o) => o.id)
              );
            } catch (txError) {
              console.error("Error fetching transaction amounts:", txError);
              // Export still proceeds — the new column just stays blank for every row.
            }
          }

      if (isSummaryView) {
        const headerRow = ["#", "Menu Item", "Total Quantity", "Reserved Items", "Available Quantity"];
        const dataRows = menuItemCounts.map((item, index) => [
          index + 1, item.name, item.totalCount, item.reservedCount,
          item.totalCount - item.reservedCount,
        ]);
        const totalRow = [
          "", "Total Items:",
          menuItemCounts.reduce((s, i) => s + i.totalCount, 0),
          menuItemCounts.reduce((s, i) => s + i.reservedCount, 0),
          menuItemCounts.reduce((s, i) => s + (i.totalCount - i.reservedCount), 0),
        ];
        const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows, totalRow]);
        ws["!cols"] = [{ wch: 5 }, { wch: 35 }, { wch: 16 }, { wch: 16 }, { wch: 20 }];
        XLSX.utils.book_append_sheet(wb, ws, (isAllCartsSelected ? "All Carts Summary" : cartName || "Summary").slice(0, 31));
        XLSX.writeFile(wb, `Menu_Item_Summary_${formattedDate}.xlsx`);

      } else if (isAllCartsSelected && groupedOrders) {
        Object.entries(groupedOrders).forEach(([cartDisplayName, orders]: [string, any]) => {
          const headerRow = ["#", "Order Reference", "Customer", "Items Ordered", "Order Value (Rs.)", "Discount (Rs.)", "Net Amount (Rs.)", "Transaction Amount (Rs.)", "Payment Method", "Agent", "Issued"];
          const dataRows = orders.map((order: any, index: number) => [
            index + 1,
            order.orderReference,
            getCustomerName(order),
            getItemsSummary(order),
            parseFloat((order.paymentSummary?.totalPrice || 0).toFixed(2)),
            parseFloat((order.paymentSummary?.discount || 0).toFixed(2)),
            parseFloat((order.paymentSummary?.netAmount || 0).toFixed(2)),
            getTransactionMismatch(order, transactionAmountsByOrderId),
            getPaymentMethod(order),
            getAgentName(order),
            "",
          ]);
          const totalRow = [
            "", "", "", "Total:",
            parseFloat(orders.reduce((s: number, o: any) => s + (o.paymentSummary?.totalPrice || 0), 0).toFixed(2)),
            parseFloat(orders.reduce((s: number, o: any) => s + (o.paymentSummary?.discount || 0), 0).toFixed(2)),
            parseFloat(orders.reduce((s: number, o: any) => s + (o.paymentSummary?.netAmount || 0), 0).toFixed(2)),
            "", "", "", "",
          ];
          const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows, totalRow]);
          ws["!cols"] = [{ wch: 5 }, { wch: 18 }, { wch: 20 }, { wch: 45 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 10 }];
          XLSX.utils.book_append_sheet(wb, ws, cartDisplayName.slice(0, 31));
        });
        XLSX.writeFile(wb, `All_Carts_Orders_${formattedDate}.xlsx`);

      } else {
        const headerRow = ["#", "Order Reference", "Customer", "Items Ordered", "Order Value (Rs.)", "Discount (Rs.)", "Net Amount (Rs.)", "Transaction Amount (Rs.)", "Payment Method", "Agent", "Issued"];
        const dataRows = filteredOrders.map((order: any, index: number) => [
          index + 1,
          order.orderReference,
          getCustomerName(order),
          getItemsSummary(order),
          parseFloat((order.paymentSummary?.totalPrice || 0).toFixed(2)),
          parseFloat((order.paymentSummary?.discount || 0).toFixed(2)),
          parseFloat((order.paymentSummary?.netAmount || 0).toFixed(2)),
          getTransactionMismatch(order, transactionAmountsByOrderId),
          getPaymentMethod(order),
          getAgentName(order),
          "",
        ]);
        const totalRow = [
          "", "", "", "Total:",
          parseFloat(filteredOrders.reduce((s, o) => s + (o.paymentSummary?.totalPrice || 0), 0).toFixed(2)),
          parseFloat(filteredOrders.reduce((s, o) => s + (o.paymentSummary?.discount || 0), 0).toFixed(2)),
          parseFloat(filteredOrders.reduce((s, o) => s + (o.paymentSummary?.netAmount || 0), 0).toFixed(2)),
          "", "", "", "",
        ];
        const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows, totalRow]);
        ws["!cols"] = [{ wch: 5 }, { wch: 18 }, { wch: 20 }, { wch: 45 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 10 }];
        XLSX.utils.book_append_sheet(wb, ws, (cartName || "Orders").slice(0, 31));
        XLSX.writeFile(wb, `${cartName}_Orders_${formattedDate}.xlsx`);
      }
    } catch (error) {
      console.error("Error generating Excel:", error);
    } finally {
      isDownloadingExcelRef.current = false;
      setIsDownloadingExcel(false);
    }
  };

  // In PrintSetupModal.tsx, add cart-wise order list handling
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePrint = () => {
    const printWindow = window.open('', '', 'height=600,width=800');
    if (!printWindow) return;

    let printContent = `
    <html>
      <head>
        <title>Orders Print</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .cart-section { margin-bottom: 40px; page-break-inside: avoid; }
          .cart-title { 
            font-size: 18px; 
            font-weight: bold; 
            color: #0066cc; 
            margin-bottom: 15px; 
            border-bottom: 2px solid #0066cc;
            padding-bottom: 5px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 20px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 8px; 
            text-align: left; 
          }
          th { 
            background-color: #f2f2f2; 
            font-weight: bold; 
          }
          .page-break { 
            page-break-before: always; 
          }
          @media print {
            .cart-section { 
              page-break-inside: avoid; 
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>Orders Report</h2>
          <p>Cart: ${cartName} | Date: ${orderDate}</p>
        </div>
  `;

    if (isAllCartsSelected) {
      // Group orders by cart for printing
      const ordersByCart: Record<string, Order[]> = {};
      
      // Use filtered orders (exclude cancelled and tentative)
      filteredOrders.forEach(order => {
        const cartName = order.cart?.displayName || "Direct Restaurant Orders";
        if (!ordersByCart[cartName]) {
          ordersByCart[cartName] = [];
        }
        ordersByCart[cartName].push(order);
      });

      Object.entries(ordersByCart).forEach(([cartName, orders], index) => {
        printContent += `
          <div class="cart-section ${index > 0 ? 'page-break' : ''}">
            <h3 class="cart-title">${cartName} (${orders.length} orders)</h3>
            <table>
              <thead>
                <tr>
                  <th>Order Ref</th>
                  <th>Creator</th>
                  <th>Status</th>
                  <th>Items</th>
                  <th>Net Amount</th>
                </tr>
              </thead>
              <tbody>
        `;

        orders.forEach(order => {
          const itemsList = order.orderItems.map(item => 
            `${item.displayName} (${item.quantity})`
          ).join(', ');

          printContent += `
            <tr>
              <td>${order.orderReference}</td>
              <td>${order.orderCreatedBy}</td>
              <td>${order.orderStatus}</td>
              <td>${itemsList}</td>
              <td>Rs. ${(order.paymentSummary?.netAmount || 0).toFixed(2)}</td>
            </tr>
          `;
        });

        printContent += `
              </tbody>
            </table>
            <div style="text-align: right; font-weight: bold; margin-top: 10px;">
              Total Net Amount: Rs. ${orders
                .reduce((sum, order) => sum + (order.paymentSummary?.netAmount || 0), 0)
                .toFixed(2)}
            </div>
          </div>
        `;
      });
    } else {
      // Single cart orders
      printContent += `
        <table>
          <thead>
            <tr>
              <th>Order Ref</th>
              <th>Creator</th>
              <th>Status</th>
              <th>Items</th>
              <th>Net Amount</th>
            </tr>
          </thead>
          <tbody>
      `;

      // Use filtered orders (exclude cancelled and tentative)
      filteredOrders.forEach(order => {
        const itemsList = order.orderItems.map(item => 
          `${item.displayName} (${item.quantity})`
        ).join(', ');

        printContent += `
          <tr>
            <td>${order.orderReference}</td>
            <td>${order.orderCreatedBy}</td>
            <td>${order.orderStatus}</td>
            <td>${itemsList}</td>
            <td>Rs. ${(order.paymentSummary?.netAmount || 0).toFixed(2)}</td>
          </tr>
        `;
      });

      printContent += `
          </tbody>
        </table>
        <div style="text-align: right; font-weight: bold; margin-top: 20px;">
          Total Net Amount: Rs. ${filteredOrders
            .reduce((sum, order) => sum + (order.paymentSummary?.netAmount || 0), 0)
            .toFixed(2)}
        </div>
      `;
    }

    printContent += `
      </body>
    </html>
  `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
    
    handleModalClose();
  };

  return (
    <Modal
      show={showModal}
      onHide={handleModalClose}
      backdrop="static"
      dialogClassName="modal-95w"
      centered
    >
      <Modal.Header closeButton>
        <div className="d-flex justify-content-end align-items-center gap-3 w-100">
          <button
            className="btn btn-sm btn-success d-flex align-items-center gap-1"
            onClick={!isDownloadingExcel ? handleDownloadExcel : undefined}
            disabled={isDownloadingExcel}
            title="Download as Excel"
            style={{ opacity: isDownloadingExcel ? 0.6 : 1 }}
          >
            <FontAwesomeIcon icon={faFileExcel} />
            <span>{isDownloadingExcel ? "Downloading..." : "Excel"}</span>
          </button>
          <button
            className="btn btn-sm btn-danger d-flex align-items-center gap-1"
            onClick={!isDownloading ? handleDownloadPdf : undefined}
            disabled={isDownloading}
            title="Download as PDF"
            style={{ opacity: isDownloading ? 0.6 : 1 }}
          >
            <FontAwesomeIcon icon={faFilePdf} />
            <span>{isDownloading ? "Downloading..." : "PDF"}</span>
          </button>
        </div>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <div id="printable-area">
          {isSummaryView ? (
            // Summary view content
            <>
              <div className="header-text-pop-up text-center mb-3">
                {isAllCartsSelected
                  ? `Menu Item Summary For All Carts on ${formattedDate}`
                  : `Menu Item Summary For ${cartName} on ${formattedDate}`}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Menu Item</th>
                      <th className="text-center">Total Quantity</th>
                      <th className="text-center">Reserved Items</th>
                      <th className="text-center">Available Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {menuItemCounts.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.name}</td>
                        <td className="text-center">{item.totalCount}</td>
                        <td className="text-center">{item.reservedCount}</td>
                        <td className="text-center">
                          {item.totalCount - item.reservedCount}
                        </td>
                      </tr>
                    ))}
                    <tr className="fw-bold">
                      <td colSpan={2} className="text-end">
                        Total Items:
                      </td>
                      <td className="text-center">
                        {menuItemCounts.reduce(
                          (sum, item) => sum + item.totalCount,
                          0
                        )}
                      </td>
                      <td className="text-center">
                        {menuItemCounts.reduce(
                          (sum, item) => sum + item.reservedCount,
                          0
                        )}
                      </td>
                      <td className="text-center">
                        {menuItemCounts.reduce(
                          (sum, item) =>
                            sum + (item.totalCount - item.reservedCount),
                          0
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          ) : isAllCartsSelected && groupedOrders ? (
            // Grouped order view content
            <>
              {Object.entries(groupedOrders).map(
                ([cartDisplayName, orders]: [string, any]) => (
                  <div key={cartDisplayName} className="mb-4">
                    <h5 className="fw-bold text-primary mb-3">
                      {cartDisplayName}
                    </h5>
                    <table className="table table-bordered table-sm mb-4">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Order Reference</th>
                          <th>Customer</th>
                          <th>Items Ordered</th>
                          <th>Order Value</th>
                          <th>Discount</th>
                          <th>Net Amount</th>
                          <th>Payment Method</th>
                          <th>Agent</th>
                          <th>Issued</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order: any, index: number) => (
                          <tr key={order.id}>
                            <td>{index + 1}</td>
                            <td>{order.orderReference}</td>
                            <td>{getCustomerName(order)}</td>
                            <td>
                              {order.orderItems.map((item: any, i: number) => (
                                <div key={i}>
                                  {item.displayName} x {item.quantity} ( Rs.{" "}
                                  {(item.unitPrice || 0).toFixed(2)})
                                </div>
                              ))}
                            </td>
                            <td>Rs. {(order.paymentSummary?.totalPrice || 0).toFixed(2)}</td>
                            <td>Rs. {(order.paymentSummary?.discount || 0).toFixed(2)}</td>
                            <td>Rs. {(order.paymentSummary?.netAmount || 0).toFixed(2)}</td>
                            <td>{getPaymentMethod(order)}</td>
                            <td>{getAgentName(order)}</td>
                            <td></td>
                          </tr>
                        ))}
                        <tr className="fw-bold">
                          <td colSpan={4} className="text-end">
                            Total:
                          </td>
                          <td>
                            Rs.{" "}
                            {orders
                              .reduce(
                                (sum: number, order: any) =>
                                  sum + (order.paymentSummary?.totalPrice || 0),
                                0
                              )
                              .toFixed(2)}
                          </td>
                          <td>
                            Rs.{" "}
                            {orders
                              .reduce(
                                (sum: number, order: any) =>
                                  sum + (order.paymentSummary?.discount || 0),
                                0
                              )
                              .toFixed(2)}
                          </td>
                          <td>
                            Rs.{" "}
                            {orders
                              .reduce(
                                (sum: number, order: any) =>
                                  sum + (order.paymentSummary?.netAmount || 0),
                                0
                              )
                              .toFixed(2)}
                          </td>
                          <td></td>
                          <td></td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </>
          ) : (
            // Single cart order view content
            <>
              <div className="header-text-pop-up text-center mb-3">
                {`Order Summary For ${cartName} on ${formattedDate}`}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Order Reference</th>
                      <th>Customer</th>
                      <th>Items Ordered</th>
                      <th>Order Value</th>
                      <th>Discount</th>
                      <th>Net Amount</th>
                      <th>Payment Method</th>
                      <th>Agent</th>
                      <th>Issued</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order, index) => (
                      <tr key={order.id}>
                        <td>{index + 1}</td>
                        <td>{order.orderReference}</td>
                        <td>{getCustomerName(order)}</td>
                        <td>
                          {order.orderItems.map((item, i) => (
                            <div key={i}>
                              {item.displayName} x {item.quantity} ( Rs.{" "}
                              {(item.unitPrice || 0).toFixed(2)})
                            </div>
                          ))}
                        </td>
                        <td>Rs. {(order.paymentSummary?.totalPrice || 0).toFixed(2)}</td>
                        <td>Rs. {(order.paymentSummary?.discount || 0).toFixed(2)}</td>
                        <td>Rs. {(order.paymentSummary?.netAmount || 0).toFixed(2)}</td>
                        <td>{getPaymentMethod(order)}</td>
                        <td>{getAgentName(order)}</td>
                        <td></td>
                      </tr>
                    ))}
                    <tr className="fw-bold">
                      <td colSpan={4} className="text-end">
                        Total:
                      </td>
                      <td>
                        Rs.{" "}
                        {filteredOrders
                          .reduce((sum, order) => sum + (order.paymentSummary?.totalPrice || 0), 0)
                          .toFixed(2)}
                      </td>
                      <td>
                        Rs.{" "}
                        {filteredOrders
                          .reduce((sum, order) => sum + (order.paymentSummary?.discount || 0), 0)
                          .toFixed(2)}
                      </td>
                      <td>
                        Rs.{" "}
                        {filteredOrders
                          .reduce((sum, order) => sum + (order.paymentSummary?.netAmount || 0), 0)
                          .toFixed(2)}
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PrintSetupModal;